import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { usePersona } from '../hooks/usePersona';
import { selectUser } from '../../store/selectors/user.selectors';
import { getStaffAvatarManifest } from '../../api/staff-avatars.api';
import {
  DEFAULT_AVATAR_BASE_URL,
  type StaffAvatarList,
  type StaffAvatarManifest,
} from '../../utils/avatarUrl';

const EMPTY_MANIFEST: StaffAvatarManifest = Object.freeze({});
const STORAGE_KEY = 'staff-avatar-list';
/** How long a fetched list is trusted before asking fir-login again; a new photo shows within it. */
export const STAFF_AVATAR_MANIFEST_TTL_MS = 24 * 60 * 60 * 1000;

interface StoredList {
  readonly savedAt: number;
  readonly list: StaffAvatarList;
}
export const STAFF_AVATAR_MANIFEST_QUERY_KEY = ['staff-avatar-manifest'];

/**
 * Undefined outside the Cowbell persona: staff avatars are a Cowbell-persona feature, and a
 * shared surface renders what it rendered before. Inside it, the manifest, which is empty
 * until it loads (or if it cannot), so every avatar shows initials.
 */
const StaffAvatarManifestContext = React.createContext<
  StaffAvatarManifest | undefined
>(undefined);

export const useStaffAvatarManifest = () =>
  React.useContext(StaffAvatarManifestContext);

/**
 * Whether a manifest has loaded in the Cowbell persona (fetched now, or kept from an earlier
 * session). A surface that already showed photos keeps its old photo source until this is
 * true, so nobody's photo turns into initials while the manifest is unavailable.
 */
const StaffAvatarsLiveContext = React.createContext(false);

export const useStaffAvatarsLive = () =>
  React.useContext(StaffAvatarsLiveContext);

/** The environment's photo folder, from the list; production's until the list has loaded. */
const StaffAvatarBaseUrlContext = React.createContext(DEFAULT_AVATAR_BASE_URL);

export const useStaffAvatarBaseUrl = () =>
  React.useContext(StaffAvatarBaseUrlContext);

function readStoredList(): StoredList | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : undefined;
    if (
      parsed &&
      typeof parsed.savedAt === 'number' &&
      typeof parsed.list?.baseUrl === 'string' &&
      parsed.list?.avatars
    ) {
      return parsed as StoredList;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function storeList(list: StaffAvatarList) {
  try {
    const stored: StoredList = { savedAt: Date.now(), list };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage full or blocked: the next session fetches it again.
  }
}

/**
 * Fetches who has a photo, then keeps it in localStorage for a day: a copy younger than
 * STAFF_AVATAR_MANIFEST_TTL_MS is used with no request at all; an older one paints at once
 * and is refreshed in the background.
 */
export function StaffAvatarManifestProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const persona = usePersona();
  // Mounted above the public routes too, and the persona outlives a logout in persisted state:
  // wait for the signed-in user (set once /me answers) so the login page asks for nothing.
  const isSignedIn = Boolean(useSelector(selectUser)?.id);
  const enabled = Boolean(persona.isCowbell) && isSignedIn;

  const [stored] = React.useState(readStoredList);

  const { data } = useQuery({
    queryKey: STAFF_AVATAR_MANIFEST_QUERY_KEY,
    queryFn: getStaffAvatarManifest,
    enabled,
    staleTime: STAFF_AVATAR_MANIFEST_TTL_MS,
    refetchOnWindowFocus: false,
    retry: retryUnlessClientError,
    initialData: stored?.list,
    initialDataUpdatedAt: stored?.savedAt,
    onSuccess: storeList,
  });

  const value = enabled ? data?.avatars ?? EMPTY_MANIFEST : undefined;
  const isLive = enabled && data !== undefined;

  return (
    <StaffAvatarManifestContext.Provider value={value}>
      <StaffAvatarsLiveContext.Provider value={isLive}>
        <StaffAvatarBaseUrlContext.Provider
          value={data?.baseUrl ?? DEFAULT_AVATAR_BASE_URL}
        >
          {children}
        </StaffAvatarBaseUrlContext.Provider>
      </StaffAvatarsLiveContext.Provider>
    </StaffAvatarManifestContext.Provider>
  );
}

/** One retry for a network or server error; none for a 4xx, which will not change. */
export function retryUnlessClientError(failureCount: number, error: unknown) {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  if (status && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < 1;
}

/**
 * For tests and stories: a fixed manifest, or undefined to stand in for another persona.
 * `live` defaults to true whenever there is a manifest.
 */
export function StaffAvatarManifestTestProvider({
  manifest,
  live = manifest !== undefined,
  children,
}: Readonly<{
  manifest: StaffAvatarManifest | undefined;
  live?: boolean;
  children: React.ReactNode;
}>) {
  return (
    <StaffAvatarManifestContext.Provider value={manifest}>
      <StaffAvatarsLiveContext.Provider value={live}>
        {children}
      </StaffAvatarsLiveContext.Provider>
    </StaffAvatarManifestContext.Provider>
  );
}
